import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { commerceConfig, OrderTrackingInfo } from "../config/commerce";

export interface Address {
  id: string;
  fullName: string;
  phone: string;
  address: string;
  apartment?: string;
  city: string;
  state: string;
  pincode: string;
  isDefault?: boolean;
}

export interface UserProfile {
  id: number | string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  skinType?: string;
}

export interface OrderItem {
  id: string;
  title: string;
  price: number;
  qty: number;
  image?: string;
}

export interface OrderRecord {
  orderId: string;
  databaseId: number;
  date: string;
  status: string;
  total: number;
  subtotal: number;
  discount: number;
  shippingFee: number;
  shippingMethod: string;
  paymentMethod: string;
  paymentId?: string;
  paymentOption: "prepaid" | "partial_cod";
  upfrontPaid: number;
  balanceDue: number;
  tracking?: OrderTrackingInfo;
  customer?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    apartment?: string;
    city: string;
    state: string;
    pincode: string;
  };
  items: OrderItem[];
}

interface AuthContextType {
  user: UserProfile | null;
  token: string | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  addresses: Address[];
  orders: OrderRecord[];
  sendOtp: (email: string, type: "login" | "signup") => Promise<{ success: boolean; message: string; dev_otp?: string }>;
  verifyOtpAndLogin: (email: string, otp: string) => Promise<{ success: boolean; message: string }>;
  verifyOtpAndSignup: (data: {
    email: string;
    otp: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  addAddress: (addr: Omit<Address, "id">) => Promise<{ success: boolean; message: string }>;
  updateAddress: (id: string, addr: Partial<Address>) => Promise<{ success: boolean; message: string }>;
  deleteAddress: (id: string) => Promise<{ success: boolean; message: string }>;
  setDefaultAddress: (id: string) => Promise<{ success: boolean; message: string }>;
  updateProfile: (profile: Partial<UserProfile>) => Promise<{ success: boolean; message: string }>;
  refreshUserData: () => Promise<void>;
  // Auth Modal State
  isAuthModalOpen: boolean;
  authModalMode: "login" | "signup";
  openAuthModal: (mode?: "login" | "signup", onSuccess?: () => void) => void;
  closeAuthModal: () => void;
  onAuthSuccessCallback?: (() => void) | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

const MAX_ADDRESSES = 3;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal State
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"login" | "signup">("login");
  const [onAuthSuccessCallback, setOnAuthSuccessCallback] = useState<(() => void) | null>(null);

  const openAuthModal = useCallback((mode: "login" | "signup" = "login", onSuccess?: () => void) => {
    setAuthModalMode(mode);
    if (onSuccess) {
      setOnAuthSuccessCallback(() => onSuccess);
    } else {
      setOnAuthSuccessCallback(null);
    }
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
    setOnAuthSuccessCallback(null);
  }, []);

  // Fetch current user details from DB
  const refreshUserData = useCallback(async (authToken?: string) => {
    const currentToken = authToken || (typeof window !== "undefined" ? localStorage.getItem("meoora_auth_token") : null);
    if (!currentToken) {
      setUser(null);
      setAddresses([]);
      setOrders([]);
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${commerceConfig.apiUrl}/user/me`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          "Content-Type": "application/json",
        },
      });

      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          setUser(json.data.user || null);
          setAddresses((json.data.addresses || []).slice(0, MAX_ADDRESSES));
          setOrders(json.data.orders || []);
          setToken(currentToken);
        } else {
          // Token invalid or expired
          if (typeof window !== "undefined") {
            localStorage.removeItem("meoora_auth_token");
          }
          setToken(null);
          setUser(null);
          setAddresses([]);
          setOrders([]);
        }
      } else {
        if (typeof window !== "undefined") {
          localStorage.removeItem("meoora_auth_token");
        }
        setToken(null);
        setUser(null);
        setAddresses([]);
        setOrders([]);
      }
    } catch (err) {
      console.error("Failed to load user profile:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize from localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedToken = localStorage.getItem("meoora_auth_token");
      if (savedToken) {
        refreshUserData(savedToken);
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [refreshUserData]);

  // Merge Guest Data into DB on successful login/signup
  const mergeGuestDataIntoDb = async (authToken: string, dbCart: any[], dbWishlist: any[]) => {
    if (typeof window === "undefined") return;

    try {
      // 1. Merge Guest Cart
      let guestCart: any[] = [];
      try {
        guestCart = JSON.parse(localStorage.getItem("meoora_cart") || "[]");
      } catch {
        guestCart = [];
      }

      let mergedCart = [...(dbCart || [])];
      for (const guestItem of guestCart) {
        const found = mergedCart.find((i) => String(i.id) === String(guestItem.id));
        if (found) {
          found.qty = (found.qty || 1) + (guestItem.qty || 1);
        } else {
          mergedCart.push(guestItem);
        }
      }

      // 2. Merge Guest Wishlist
      let guestWishlist: any[] = [];
      try {
        guestWishlist = JSON.parse(localStorage.getItem("meoora_wishlist") || "[]");
      } catch {
        guestWishlist = [];
      }

      let mergedWishlist = [...(dbWishlist || [])];
      for (const guestItem of guestWishlist) {
        if (!mergedWishlist.some((i) => String(i.id) === String(guestItem.id))) {
          mergedWishlist.push(guestItem);
        }
      }

      // Sync merged state to DB
      await fetch(`${commerceConfig.apiUrl}/user/sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          cart: mergedCart,
          wishlist: mergedWishlist,
        }),
      });

      // Update localStorage so CartContext and WishlistContext read fresh merged state
      localStorage.setItem("meoora_cart", JSON.stringify(mergedCart));
      localStorage.setItem("meoora_wishlist", JSON.stringify(mergedWishlist));
      window.dispatchEvent(new Event("meoora_cart_updated"));
      window.dispatchEvent(new Event("meoora_wishlist_updated"));
    } catch (err) {
      console.error("Error merging guest data:", err);
    }
  };

  // Send OTP
  const sendOtp = async (email: string, type: "login" | "signup") => {
    try {
      const res = await fetch(`${commerceConfig.apiUrl}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, type }),
      });
      const data = await res.json();
      return {
        success: Boolean(data.success),
        message: data.message || "Failed to send code.",
        dev_otp: data.dev_otp,
      };
    } catch (err: any) {
      return {
        success: false,
        message: err?.message || "Network error. Please try again.",
      };
    }
  };

  // Verify OTP for Login
  const verifyOtpAndLogin = async (email: string, otp: string) => {
    try {
      const res = await fetch(`${commerceConfig.apiUrl}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, type: "login" }),
      });
      const data = await res.json();

      if (data.success && data.data) {
        const authToken = data.data.token;
        if (typeof window !== "undefined") {
          localStorage.setItem("meoora_auth_token", authToken);
        }
        setToken(authToken);
        setUser(data.data.user || null);
        setAddresses((data.data.addresses || []).slice(0, MAX_ADDRESSES));
        setOrders(data.data.orders || []);

        // Merge guest data
        await mergeGuestDataIntoDb(authToken, data.data.cart || [], data.data.wishlist || []);

        closeAuthModal();
        if (onAuthSuccessCallback) {
          onAuthSuccessCallback();
        }

        return { success: true, message: data.message || "Signed in successfully!" };
      }

      return { success: false, message: data.message || "Verification failed." };
    } catch (err: any) {
      return { success: false, message: err?.message || "Network error." };
    }
  };

  // Verify OTP for Signup
  const verifyOtpAndSignup = async ({
    email,
    otp,
    firstName,
    lastName,
    phone,
  }: {
    email: string;
    otp: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }) => {
    try {
      const res = await fetch(`${commerceConfig.apiUrl}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, firstName, lastName, phone, type: "signup" }),
      });
      const data = await res.json();

      if (data.success && data.data) {
        const authToken = data.data.token;
        if (typeof window !== "undefined") {
          localStorage.setItem("meoora_auth_token", authToken);
        }
        setToken(authToken);
        setUser(data.data.user || null);
        setAddresses((data.data.addresses || []).slice(0, MAX_ADDRESSES));
        setOrders(data.data.orders || []);

        // Merge guest data
        await mergeGuestDataIntoDb(authToken, data.data.cart || [], data.data.wishlist || []);

        closeAuthModal();
        if (onAuthSuccessCallback) {
          onAuthSuccessCallback();
        }

        return { success: true, message: data.message || "Account created successfully!" };
      }

      return { success: false, message: data.message || "Signup failed." };
    } catch (err: any) {
      return { success: false, message: err?.message || "Network error." };
    }
  };

  // Logout
  const logout = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("meoora_auth_token");
      // Note: we can either keep or clear cart on logout; keep empty for clean session
      localStorage.setItem("meoora_cart", "[]");
      localStorage.setItem("meoora_wishlist", "[]");
      window.dispatchEvent(new Event("meoora_cart_updated"));
      window.dispatchEvent(new Event("meoora_wishlist_updated"));
    }
    setToken(null);
    setUser(null);
    setAddresses([]);
    setOrders([]);
  };

  // Sync helpers with DB
  const syncAddressesToDb = async (newAddresses: Address[]) => {
    if (!token) return;
    try {
      await fetch(`${commerceConfig.apiUrl}/user/sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ addresses: newAddresses }),
      });
    } catch (err) {
      console.error("Failed to sync addresses to DB:", err);
    }
  };

  // 1. Add Address (HARD LIMIT OF 3 ENFORCED)
  const addAddress = async (addr: Omit<Address, "id">): Promise<{ success: boolean; message: string }> => {
    if (addresses.length >= MAX_ADDRESSES) {
      return {
        success: false,
        message: `Maximum limit of ${MAX_ADDRESSES} saved addresses reached. Please edit or remove an existing address.`,
      };
    }

    const newAddr: Address = {
      ...addr,
      id: `addr_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      isDefault: addresses.length === 0 ? true : Boolean(addr.isDefault),
    };

    let updated = [...addresses];
    if (newAddr.isDefault) {
      updated = updated.map((a) => ({ ...a, isDefault: false }));
    }
    updated.push(newAddr);
    updated = updated.slice(0, MAX_ADDRESSES);

    setAddresses(updated);
    await syncAddressesToDb(updated);

    return { success: true, message: "Address saved successfully." };
  };

  // 2. Update Address
  const updateAddress = async (id: string, partial: Partial<Address>): Promise<{ success: boolean; message: string }> => {
    let updated = addresses.map((a) => (a.id === id ? { ...a, ...partial } : a));
    if (partial.isDefault) {
      updated = updated.map((a) => (a.id === id ? { ...a, isDefault: true } : { ...a, isDefault: false }));
    }
    setAddresses(updated);
    await syncAddressesToDb(updated);
    return { success: true, message: "Address updated successfully." };
  };

  // 3. Delete Address
  const deleteAddress = async (id: string): Promise<{ success: boolean; message: string }> => {
    let updated = addresses.filter((a) => a.id !== id);
    if (updated.length > 0 && !updated.some((a) => a.isDefault)) {
      updated[0].isDefault = true;
    }
    setAddresses(updated);
    await syncAddressesToDb(updated);
    return { success: true, message: "Address removed successfully." };
  };

  // 4. Set Default Address
  const setDefaultAddress = async (id: string): Promise<{ success: boolean; message: string }> => {
    const updated = addresses.map((a) => ({
      ...a,
      isDefault: a.id === id,
    }));
    setAddresses(updated);
    await syncAddressesToDb(updated);
    return { success: true, message: "Default address updated." };
  };

  // 5. Update Profile
  const updateProfile = async (profile: Partial<UserProfile>): Promise<{ success: boolean; message: string }> => {
    if (!token) return { success: false, message: "Unauthorized." };
    try {
      const res = await fetch(`${commerceConfig.apiUrl}/user/sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ profile }),
      });
      const data = await res.json();
      if (data.success && data.data?.user) {
        setUser(data.data.user);
        return { success: true, message: "Profile details updated." };
      }
      return { success: false, message: data.message || "Failed to update profile." };
    } catch (err: any) {
      return { success: false, message: err?.message || "Network error." };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoggedIn: Boolean(token && user),
        isLoading,
        addresses,
        orders,
        sendOtp,
        verifyOtpAndLogin,
        verifyOtpAndSignup,
        logout,
        addAddress,
        updateAddress,
        deleteAddress,
        setDefaultAddress,
        updateProfile,
        refreshUserData,
        isAuthModalOpen,
        authModalMode,
        openAuthModal,
        closeAuthModal,
        onAuthSuccessCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
